import {useCallback, useRef, useState} from 'react'
import {fetchNationalSponsorAssignments} from '../api/sponsorAssignments'
import {AppState} from '../store/store'
import {SponsorImageKey} from '../types/lessonKeys'
import {ModuleSponsorAssignment} from '../types/sponsorAssignments'

// Define the 4 modules that national sponsors can be assigned to
const ALL_MODULE_KEYS: SponsorImageKey[] = [
  'MODULE_ONE',
  'MODULE_TWO',
  'MODULE_THREE',
  'MODULE_FOUR'
]

// Create default assignments with no sponsor assigned to any module
const DEFAULT_ASSIGNMENTS: ModuleSponsorAssignment[] = ALL_MODULE_KEYS.map(
  moduleKey => ({
    moduleKey,
    sponsorId: null
  })
)

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: (assignments: ModuleSponsorAssignment[]) => void
}>

type UseNationalSponsorAssignmentsReturn = {
  assignments: ModuleSponsorAssignment[]
  isLoading: boolean
  error: Error | null
  fetchAssignments: (state: AppState) => void
  clearAssignments: () => void
  updateAssignments: (assignments: ModuleSponsorAssignment[]) => void
}

export function useNationalSponsorAssignments({
  onError,
  onSuccess
}: Options = {}): UseNationalSponsorAssignmentsReturn {
  const [assignments, setAssignments] =
    useState<ModuleSponsorAssignment[]>(DEFAULT_ASSIGNMENTS)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Use refs to avoid dependency issues
  const onErrorRef = useRef(onError)
  const onSuccessRef = useRef(onSuccess)

  // Update refs when callbacks change
  onErrorRef.current = onError
  onSuccessRef.current = onSuccess

  const fetchAssignments = useCallback((state: AppState): void => {
    setIsLoading(true)
    setError(null)

    fetchNationalSponsorAssignments(state)
      .then(result => {
        setAssignments(result.assignments)
        onSuccessRef.current?.(result.assignments)
      })
      .catch(err => {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch national sponsor assignments')
        setError(error)
        onErrorRef.current?.(error)
      })
      .finally(() => setIsLoading(false))
  }, [])

  const clearAssignments = useCallback((): void => {
    setAssignments(DEFAULT_ASSIGNMENTS)
    setError(null)
  }, [])

  const updateAssignments = useCallback(
    (newAssignments: ModuleSponsorAssignment[]): void => {
      setAssignments(newAssignments)
    },
    []
  )

  return {
    assignments,
    isLoading,
    error,
    fetchAssignments,
    clearAssignments,
    updateAssignments
  }
}
